import { Link, ModuleBase, ResponsiveImage } from '@/components';
import { CategoryModel, LinkModel, ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import { addOpacityToColor, colorFromBgColor } from '@/utils';
import Flicking from '@egjs/react-flicking';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

type InnovationPlatformsModuleModel = ModuleBaseModel<
  {
    innovationPlatforms: CategoryModel[];
    text: string;
    title: string;
    cta?: LinkModel;
  } & ModuleBasePropsModel
>;

const InnovationPlatformsModule = (data: InnovationPlatformsModuleModel) => {
  const { innovationPlatforms, text, title, cta, backgroundColour } = data.props;
  const carousel = useRef<Flicking>(null);
  const t = useTranslations();

  const onFlickingMove = () => {
    const flicking = carousel.current;
    if (!flicking) return;
    const panels = flicking.visiblePanels;
    panels.forEach((panel) => {
      const progress = panel.outsetProgress;
      const target = panel.element;

      const scale = 1 - Math.abs(progress * 0.5);
      target.style.transform = `scale(${scale})`;
      target.style.zIndex = (50 - Math.floor(progress * 10)).toString();
      if (progress < -0.2 || progress > 0.9) {
        target.style.opacity = '0';
      } else {
        target.style.opacity = '1';
      }
    });
  };

  return (
    <ModuleBase data={data} className="overflow-hidden md:!pt-9">
      <div className="container flex flex-col gap-6 pt-5 pb-14 md:py-5 lg:py-0">
        <div className="flex">
          <div className={classNames(cta ? 'w-8/12' : 'w-full')}>
            {title && (
              <h2 className="color-from-bg text-[22px] leading-7 font-light lg:text-[40px] lg:leading-[48px]">
                {title}
              </h2>
            )}
          </div>

          {cta && (
            <div className="w-4/12 text-right">
              <Link
                link={cta}
                className="btn secondary btn-from-bg"
                style={{
                  color: `${colorFromBgColor(backgroundColour)} !important`,
                  borderColor: `${addOpacityToColor(colorFromBgColor(backgroundColour), 0.2)} !important`,
                }}
              >
                {t('general.$viewMore')}
              </Link>
            </div>
          )}
        </div>

        {text && <div className="text-container color-from-bg w-full lg:w-8/12 xl:w-6/12">{text}</div>}

        <div className="list-container xxl:pl-0 hidden pt-10 pb-8 md:flex lg:pl-1">
          {innovationPlatforms.map((platform, index) => {
            return platform.landingPageLink ? (
              <Link
                key={index}
                link={platform.landingPageLink}
                className="group ip-list-item"
                style={{ width: `${100 / innovationPlatforms.length}%` }}
              >
                <div
                  className="image-container relative z-0 scale-[0.85] transition-transform duration-300 group-hover:!z-50 group-hover:scale-[1.25]"
                  style={{
                    zIndex: 50 - index,
                  }}
                >
                  <div className="aspect-square md:w-[190px] xl:w-[240px]">
                    {platform.image && (
                      <ResponsiveImage
                        image={platform.image}
                        widths={{ xs: 360, sm: 360, md: 360, lg: 360, xl: 360 }}
                        heights={{ xs: 360, sm: 360, md: 360, lg: 360, xl: 360 }}
                      />
                    )}
                  </div>
                  <div className="absolute top-0 left-0 flex h-full items-center justify-center rounded-full bg-black/10 opacity-0 transition-opacity duration-500 group-hover:opacity-100 md:w-[190px] xl:w-[240px]">
                    {platform.categoryDisplayName && (
                      <div className="w-full px-3 text-center text-3xl text-white">{platform.categoryDisplayName}</div>
                    )}
                  </div>
                </div>
                <div className="category-name-container scale-[0.85] text-center text-xl text-white transition-opacity duration-300 group-hover:opacity-0 md:w-[190px] xl:w-[240px]">
                  {platform.categoryDisplayName}
                </div>
              </Link>
            ) : null;
          })}
        </div>

        <Flicking
          ref={carousel}
          preventClickOnDrag
          preventDefaultOnDrag
          align="prev"
          panelsPerView={3}
          horizontal
          circular
          onMove={onFlickingMove}
          onReady={onFlickingMove}
          onAfterResize={onFlickingMove}
          className="-mx-2 flex w-full !overflow-visible pt-14 md:hidden"
        >
          {innovationPlatforms.map((platform, index) => {
            return platform.landingPageLink ? (
              <Link key={index} link={platform.landingPageLink} className="opacity-0 transition-opacity">
                <div className="relative z-0 w-[190px]">
                  <div className="aspect-square w-full">
                    {platform.image && (
                      <ResponsiveImage
                        image={platform.image}
                        widths={{ xs: 360, sm: 1, md: 1, lg: 1, xl: 1 }}
                        heights={{ xs: 360, sm: 1, md: 1, lg: 1, xl: 1 }}
                      />
                    )}
                  </div>
                </div>
              </Link>
            ) : null;
          })}
        </Flicking>
      </div>
    </ModuleBase>
  );
};

export default InnovationPlatformsModule;
